import {
  ActionPerformed,
  PushNotificationSchema,
  PushNotifications,
  Token,
} from '@capacitor/push-notifications';
import {
  CreatePlatformEndpointCommand,
  CreatePlatformEndpointInput,
  DeleteEndpointCommand,
  GetEndpointAttributesCommand,
  SNSClient,
} from '@aws-sdk/client-sns';
import { Platform, isAndroid, isBrowser, isIOs } from './device';

import { NotificationPayload } from '../websocket/client';
import config from '@/config';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getUserAttributes } from '../auth';
import logger from '@/services/logger';
import { ref } from 'vue';
import router from '@/router';
import { sendRegisterEndpoint } from '../websocket';
import { useStore } from '@/store';

type NotificationNavTarget = {
  routeName: string;
  paramKey?: string;
};

// Tracks the setup belonging to the signed-in user, not to the installation:
// the SNS endpoint is registered against that user's identity, so the flag
// cannot outlive their session.
const pushInitialised = ref(false);

export const startPushNotifications = async (
  forceSetup: boolean,
): Promise<void> => {
  logger.debug('startPushNotifications');
  if (await isBrowser()) return;

  if (forceSetup) {
    logger.debug('pushNotification forcing endpoint check');
  } else if (pushInitialised.value) {
    logger.debug('pushNotification skipping duplicate initialisation');
    return;
  }

  // Request permission
  const permStatus = await PushNotifications.checkPermissions();

  let finalStatus = permStatus.receive;
  if (finalStatus === 'prompt' || finalStatus === 'prompt-with-rationale') {
    const requested = await PushNotifications.requestPermissions();
    finalStatus = requested.receive;
  }

  if (finalStatus !== 'granted') {
    logger.warn('pushNotification permission not granted', { finalStatus });
    return;
  }

  // Android notification channel - required for control over sound/importance/vibration on API 26+
  if (await isAndroid()) {
    await PushNotifications.createChannel({
      id: 'default',
      name: 'General Notifications',
      description: 'General app notifications',
      importance: 4, // high
      visibility: 1, // public
      vibration: true,
    });
  }

  // Remove any existing listeners before re-adding
  await PushNotifications.removeAllListeners();

  PushNotifications.addListener('registration', async (token: Token) => {
    logger.debug('pushNotification registration');

    const store = useStore();
    if (store.pushRegistrationToken !== token.value) {
      logger.debug('Detected new token, refreshing endpoint');
    } else {
      logger.debug('Token unchanged, checking endpoint status');
    }

    try {
      await sendPushEndpoint(token.value);
      store.pushRegistrationToken = token.value;
    } catch (err: any) {
      logger.error('pushNotification registration', {
        error: err.message ?? err,
      });
      store.pushRegistrationToken = undefined;
      store.pushEndpointArn = undefined;
    }
  });

  PushNotifications.addListener('registrationError', (error) => {
    logger.error('pushNotification registrationError', error);
  });

  // Foreground notification received - handled by OS
  PushNotifications.addListener(
    'pushNotificationReceived',
    async (notification: PushNotificationSchema) => {
      logger.debug('pushNotification pushNotificationReceived', notification);
    },
  );

  // Tapped from notification tray (background/killed)
  PushNotifications.addListener(
    'pushNotificationActionPerformed',
    async (action: ActionPerformed) => {
      logger.debug('pushNotification pushNotificationActionPerformed', action);

      await handleNotificationTap(action.notification);
    },
  );

  await PushNotifications.register();

  pushInitialised.value = true;

  logger.debug('pushNotifications started');
};

export const stopPushNotifications = async (): Promise<void> => {
  logger.debug('stopPushNotifications');
  if (await isBrowser()) return;

  await PushNotifications.removeAllListeners();
  pushInitialised.value = false;
};

export const sendPushEndpoint = async (
  registrationToken: string,
): Promise<void> => {
  if (registrationToken === undefined) {
    return;
  }

  const { credentials } = await fetchAuthSession();
  const store = useStore();

  const client = new SNSClient({
    region: config.services.sns.region,
    credentials,
  });

  let platform = '';
  let PlatformApplicationArn = undefined;
  if (await isAndroid()) {
    PlatformApplicationArn = config.services.sns.androidPlatformArn;
    platform = Platform.ANDROID;
  } else if (await isIOs()) {
    PlatformApplicationArn = config.services.sns.iOsPlatformArn;
    platform = Platform.IOS;
  }

  if (!PlatformApplicationArn) {
    return;
  }

  if (
    registrationToken === store.pushRegistrationToken &&
    store.pushEndpointArn
  ) {
    if (await endpointExistsAndEnabled(client, store.pushEndpointArn)) {
      logger.debug('pushNotification skipping enabled endpoint', {
        endpoint: store.pushEndpointArn,
      });
      await registerPushEndpoint(platform, store.pushEndpointArn);
      return;
    }
  }

  try {
    const payload: CreatePlatformEndpointInput = {
      PlatformApplicationArn,
      Token: registrationToken,
    };
    const command = new CreatePlatformEndpointCommand(payload);
    const response = await client.send(command);
    logger.debug('sendPushEndpoint', { response });

    if (response.EndpointArn) {
      // SNS CreatePlatformEndpoint is idempotent — it may return an existing
      // disabled endpoint. Delete it and recreate to get a fresh active one.
      const enabled = await endpointExistsAndEnabled(
        client,
        response.EndpointArn,
      );
      if (!enabled) {
        logger.debug(
          'sendPushEndpoint: disabled endpoint returned, recreating',
          { arn: response.EndpointArn },
        );
        await client.send(
          new DeleteEndpointCommand({ EndpointArn: response.EndpointArn }),
        );
        const fresh = await client.send(
          new CreatePlatformEndpointCommand(payload),
        );
        if (fresh.EndpointArn) {
          await registerPushEndpoint(platform, fresh.EndpointArn);
        }
        return;
      }
      await registerPushEndpoint(platform, response.EndpointArn);
    }
  } catch (err: any) {
    logger.error('sendPushEndpoint', { error: err.message ?? err });
    throw err;
  }
};

export const registerPushEndpoint = async (
  platform: string,
  endpoint: string,
) => {
  try {
    const attributes = await getUserAttributes();
    sendRegisterEndpoint(attributes.email, platform, endpoint);
    useStore().pushEndpointArn = endpoint;
  } catch (err: any) {
    logger.error('registerPushEndpoint', { error: err.message ?? err });
    throw err;
  }
};

const endpointExistsAndEnabled = async (
  client: SNSClient,
  endpointArn: string,
) => {
  try {
    const command = new GetEndpointAttributesCommand({
      EndpointArn: endpointArn,
    });
    const response = await client.send(command);
    logger.debug('Endpoint Attributes:', response.Attributes);
    return response.Attributes?.Enabled === 'true';
  } catch (err: any) {
    logger.error('getEndpointStatus', { error: err.message ?? err });
    return false;
  }
};

const NOTIFICATION_TYPE_ROUTES: Record<string, NotificationNavTarget> = {
  reports: { routeName: 'home', paramKey: 'reference' },
  viewBooking: { routeName: 'viewBooking', paramKey: 'reference' },
  newBooking: { routeName: 'newBooking', paramKey: 'templateId' },
  editBooking: { routeName: 'editBooking', paramKey: 'reference' },
  profile: { routeName: 'profile' },
};

export const handleNotificationTap = async (
  payload: NotificationPayload | PushNotificationSchema,
) => {
  const { type, id } = payload.data ?? {};
  if (!type) {
    logger.warn('handleNotificationTap: no notification type', { type });
    return;
  }

  const target = NOTIFICATION_TYPE_ROUTES[type];
  if (!target) {
    logger.warn('handleNotificationTap: unknown notification type', { type });
    return;
  }

  await router.replace({
    name: target.routeName,
    params: target.paramKey && id ? { [target.paramKey]: id } : undefined,
  });
};
